// apps/accounting-frontend/src/pages/TaxReportPage.tsx
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount, CustomSelect, StatCard, TableSkeleton, btnSecondary } from '@redpanda/ui';
import { toIntlLocale, useRefetchOnFocus } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { taxPeriodOptions, useTaxReport } from '../stores/reportStore';
import { useDataChanged } from '../utils/dataEvents';

/** 「標籤：金額」清單的一列 */
const Line: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between px-5 py-2.5 ${strong ? 'font-semibold text-fg' : ''}`}>
    <dt className={strong ? '' : 'text-fg-secondary'}>{label}</dt>
    <dd className="tabular-nums"><Amount value={value} /></dd>
  </div>
);

/**
 * 營業稅試算（兩個月一期，§3-16 可列印）：銷項來自本期開立的發票；進項來自記在「進項稅額」科目的已過帳分錄
 * 原本：每月一期、4 張彩色統計卡＋重複的「試算說明」區塊、用 html2canvas 截圖存 PDF（深色主題印出整片黑）
 */
const TaxReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext)!;
  const locale = toIntlLocale(i18n.language);
  const periods = taxPeriodOptions();
  const [period, setPeriod] = useState(periods[0]);
  const report = useTaxReport({ period });

  useEffect(() => {
    report.refetch();
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(report.refetch);
  useDataChanged(['invoices', 'journals'], report.refetch);

  const periodLabel = (value: string) => {
    const [year, month] = value.split('-').map(Number);
    return t('taxReports.periodLabel', { year, from: month, to: month + 1 });
  };
  const dateText = (iso: string) => new Date(iso).toLocaleDateString(locale, { timeZone: 'UTC', dateStyle: 'long' });
  const data = report.data;
  const refundable = !!data && data.netTax < 0;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('taxReports.title')}</h1>
          <div className="flex items-center space-x-3">
            <div className="w-44">
              <CustomSelect value={period} onChange={setPeriod} options={periods.map(value => ({ value, label: periodLabel(value) }))} />
            </div>
            <button type="button" onClick={() => window.print()} disabled={!data} className={`${btnSecondary} disabled:opacity-50`}>
              {t('reports.print')}
            </button>
          </div>
        </div>
      </div>

      <div className="report-print">
        <div className="mb-4">
          <p className="print-only hidden text-base font-semibold">{user?.organization?.name}</p>
          <p className="print-only hidden text-lg font-semibold">{t('taxReports.title')}</p>
          <p className="text-[13px] text-fg-secondary">
            {data ? `${dateText(data.startDate)} – ${dateText(data.endDate)}` : periodLabel(period)}
          </p>
        </div>

        {!data ? (
          <TableSkeleton rows={6} columns={2} showHeader={false} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
              <StatCard title={t('taxReports.totalOutputTax')} value={<Amount value={data.sales.outputTax} />} hint={t('taxReports.invoiceCount', { count: data.sales.invoiceCount })} />
              <StatCard title={t('taxReports.totalInputTax')} value={<Amount value={data.purchases.inputTax} />} hint={t('taxReports.entryCount', { count: data.purchases.entries.length })} />
              <StatCard
                title={refundable ? t('taxReports.refundableAmount') : t('taxReports.payableAmount')}
                value={<Amount value={Math.abs(data.netTax)} />}
                hint={t('taxReports.netFormula')}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section>
                <h2 className="text-base font-semibold text-fg mb-3">{t('taxReports.form401Title')}</h2>
                <dl className="bg-surface rounded-lg border border-line divide-y divide-line text-sm">
                  <Line label={t('taxReports.taxableAmount')} value={data.sales.taxableAmount} />
                  <Line label={t('taxReports.zeroRatedAmount')} value={data.sales.zeroRatedAmount} />
                  {(data.sales.creditNotes?.count ?? 0) > 0 && (
                    <Line label={t('taxReports.creditNotes', { count: data.sales.creditNotes.count })} value={-data.sales.creditNotes.tax} />
                  )}
                  <Line label={t('taxReports.outputTax')} value={data.sales.outputTax} strong />
                </dl>
                <p className="mt-2 text-[13px] text-fg-secondary">{t('taxReports.salesSource')}</p>
              </section>

              <section>
                <h2 className="text-base font-semibold text-fg mb-3">{t('taxReports.form403Title')}</h2>
                <dl className="bg-surface rounded-lg border border-line divide-y divide-line text-sm">
                  {data.purchases.entries.length === 0 ? (
                    <p className="px-5 py-4 text-fg-secondary">{t('taxReports.noInputEntries')}</p>
                  ) : (
                    data.purchases.entries.map(entry => (
                      <div key={entry.id} className="grid grid-cols-[4.5rem_1fr_7rem] items-center gap-4 px-5 py-2.5">
                        <dt className="tabular-nums text-fg-secondary">{new Date(entry.date).toLocaleDateString(locale, { month: 'numeric', day: 'numeric' })}</dt>
                        <dd className="truncate text-fg" title={entry.description}>
                          {entry.description}
                          {entry.reference && <span className="ml-2 text-[13px] text-fg-secondary tabular-nums">{entry.reference}</span>}
                        </dd>
                        <dd className="tabular-nums text-right text-fg"><Amount value={entry.inputTax} /></dd>
                      </div>
                    ))
                  )}
                  <Line label={t('taxReports.inputTax')} value={data.purchases.inputTax} strong />
                </dl>
                <p className="mt-2 text-[13px] text-fg-secondary">
                  {data.purchases.hasInputAccount ? t('taxReports.purchaseSource') : t('taxReports.noInputAccount')}
                </p>
              </section>
            </div>

            {refundable && <p className="mt-6 text-sm text-fg-secondary">{t('taxReports.refundNote')}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default TaxReportPage;
